import { PageWorkHealth } from './pageWorkHealth';
/**
 * EngineBroker: the main thread's owner of the engine worker
 * (design/AQUEDUCT_WORKER_MIGRATION.md). Spawns the worker, boots it with the
 * wasm module compiled by the app.html bootstrap (compileStreaming runs on
 * the browser's internal thread pool; the compiled WebAssembly.Module
 * transfers), routes pushes to ProjectHandles, and carries the auth token.
 *
 * Handles are synchronous to obtain: commands buffer in the message channel
 * behind the boot/acquire messages (FIFO), so no caller ever awaits the
 * engine to start issuing work.
 */

import { browser, version } from '$app/environment';
import {
	flushSentryConsole,
	pageConsoleContext,
	subscribeTelemetryUser
} from '../../sentryConsole';
import type { MainToWorker, WorkerToMain, FramePacket, BootStage } from './protocol';
import { ProjectHandle } from './projectHandle';
import engineWorkerUrl from './engineWorker.ts?worker&url';
import { MATERIAL_OUTPUT_ROLES, MATERIAL_ROLES, type MaterialOutputRole } from '../materialRoles';

export { MATERIAL_OUTPUT_ROLES, MATERIAL_ROLES, type MaterialOutputRole } from '../materialRoles';

/** The landing page's boot timeline logger (app.html, '/' only). */
export function bootTimelineLog(event: string, details?: Record<string, unknown>) {
	(
		window as unknown as {
			__iliadLandingBootLog?: (event: string, details?: Record<string, unknown>) => void;
		}
	).__iliadLandingBootLog?.(event, details);
}

type PendingQuery = {
	resolve: (v: unknown) => void;
	reject: (e: Error) => void;
	label: string;
	startedAt: number;
	message: MainToWorker;
};

function messageLabel(msg: MainToWorker): string {
	const method = (msg as { method?: string }).method;
	return method ? `${msg.op}:${method}` : msg.op;
}

/** The app.html bootstrap's early-start slots (idempotent). */
type EarlyBoot = {
	slangHostModulePromise?: Promise<{
		compileAqueductWebAssembly(url: string): Promise<WebAssembly.Module>;
	}>;
	compiledWasmModulePromise?: Promise<WebAssembly.Module>;
	/** Pre-boot shell worker spawned at first byte; the broker adopts it. */
	engineWorker?: Worker;
};

declare global {
	interface Window {
		__aqueductRuntime?: EarlyBoot;
	}
}

function debugGpuBootFailures(): number | undefined {
	if (!import.meta.env.DEV) return undefined;
	const raw = new URLSearchParams(location.search).get('aqueduct-debug-gpu-boot-failures');
	if (raw === null) return undefined;
	const count = Number.parseInt(raw, 10);
	return Number.isFinite(count) ? Math.max(0, Math.min(count, 10)) : undefined;
}

export class EngineBroker {
	readonly health: PageWorkHealth = new PageWorkHealth((id: number): void =>
		this.post({ op: 'healthProbe', id })
	);
	private worker: Worker;
	private nextQueryId = 1;
	private nextProjectId = 1;
	private pending = new Map<number, PendingQuery>();
	private handles = new Map<number, ProjectHandle>();
	private terminalError: Error | null = null;
	private bootedResolve!: (stages: BootStage[]) => void;
	private bootedReject!: (e: Error) => void;
	/** Resolves when the worker reports engine boot complete. */
	readonly booted = new Promise<BootStage[]>((resolve, reject) => {
		this.bootedResolve = resolve;
		this.bootedReject = reject;
	});

	constructor() {
		// Adopt the app.html pre-boot shell when one was spawned at first
		// byte: same worker, expensive boot already done or in flight. The
		// adopt message must be the shell's cue before any buffered work, so
		// it is posted synchronously here, ahead of any handle's commands.
		const preSpawned = window.__aqueductRuntime?.engineWorker;
		if (preSpawned) {
			this.worker = preSpawned;
			window.__aqueductRuntime!.engineWorker = undefined;
		} else {
			this.worker = new Worker(engineWorkerUrl, { type: 'module' });
		}
		bootTimelineLog(
			preSpawned ? 'broker: adopted pre-boot shell' : 'broker: engine worker spawned'
		);
		this.worker.onmessage = (event: MessageEvent<WorkerToMain>) => this.onMessage(event.data);
		this.worker.onerror = (event) => {
			event.preventDefault();
			this.failWorker(new Error(`Engine worker stopped: ${event.message ?? 'unknown error'}`));
		};
		this.worker.onmessageerror = (): void =>
			this.failWorker(new Error('Engine worker message could not be decoded'));
		this.booted.catch(() => {});
		// Release the worker's wasm heap and GPU device the moment the page
		// goes away: rapid refreshes otherwise stack overlapping engine
		// instances (each hundreds of MB plus a device) faster than the
		// browser reaps them, which can kill the renderer process outright.
		// A bfcache entry keeps its worker (the page may be revived).
		window.addEventListener('pagehide', (event) => {
			if (!event.persisted) {
				this.health.endAll('page_exit');
				flushSentryConsole();
				this.worker.terminate();
			} else this.health.setVisible(false);
		});
		// Forward page visibility so the worker can park its frame loop while
		// hidden (worker rAF keeps firing for hidden pages; main-thread rAF
		// does not, and hidden tabs must match that: a background tab doing
		// GPU work is what the browser's device-reclaim interventions target).
		document.addEventListener('visibilitychange', () => {
			this.health.setVisible(document.visibilityState !== 'hidden');
			this.post({ op: 'visibility', hidden: document.visibilityState === 'hidden' });
		});
		window.addEventListener('pageshow', (event: PageTransitionEvent): void => {
			if (event.persisted) {
				this.health.setVisible(document.visibilityState !== 'hidden');
				this.post({ op: 'visibility', hidden: document.visibilityState === 'hidden' });
			}
		});
		this.health.setVisible(document.visibilityState !== 'hidden');
		this.post({ op: 'visibility', hidden: document.visibilityState === 'hidden' });
		if (preSpawned) {
			this.post({
				op: 'adopt',
				consoleContext: pageConsoleContext(version),
				moduleUrl: new URL(engineWorkerUrl, location.href).href,
				origin: location.origin,
				dev: import.meta.env.DEV,
				debugGpuBootFailures: debugGpuBootFailures()
			});
			this.checkRoleDrift();
		} else {
			void this.boot().catch((error: unknown): void =>
				this.failWorker(error instanceof Error ? error : new Error(String(error)))
			);
		}
		let accountId: string | null | undefined;
		const unsubscribeTelemetry: () => void = subscribeTelemetryUser((user) => {
			if (accountId !== undefined && accountId !== (user?.id ?? null)) {
				this.health.accountChanged();
				for (const projectId of this.handles.keys())
					this.post({
						op: 'healthGeneration',
						projectId,
						generation: this.health.generation(projectId)
					});
			}
			accountId = user?.id ?? null;
			this.post({ op: 'setTelemetryUser', user });
		});
		window.addEventListener('pagehide', (event: PageTransitionEvent): void => {
			if (!event.persisted) unsubscribeTelemetry();
		});
	}

	private failWorker(error: Error) {
		if (this.terminalError) return;
		this.terminalError = error;
		this.health.observer.terminal(error);
		for (const [, entry] of this.pending) entry.reject(error);
		this.pending.clear();
		this.bootedReject(error);
		for (const handle of this.handles.values()) handle.receiveTerminalError(error);
	}

	/** Drift check: the complete TS role contract must equal the Rust core. */
	private checkRoleDrift() {
		void this.booted
			.then(() => this.moduleQuery<string>('aqueduct_material_role_contract_json'))
			.then((contractJson) => {
				if (JSON.stringify(JSON.parse(contractJson)) !== JSON.stringify(MATERIAL_ROLES)) {
					console.error(
						`MATERIAL_ROLES drift: core says ${contractJson}, ` +
							`frontend says ${JSON.stringify(MATERIAL_ROLES)}`
					);
				}
			})
			.catch(() => {});
	}

	private async boot() {
		// Reuse (or create) the app.html early-start compile so the wasm fetch +
		// compile begins at first byte on prewarmed routes and exactly once
		// everywhere else.
		const state = (window.__aqueductRuntime ??= {});
		const { AQUEDUCT_PROJECT_WASM_PATH, AQUEDUCT_SLANG_BROWSER_HOST_PATH } = await import(
			'$lib/generated/aqueductRuntimeAssets'
		);
		state.slangHostModulePromise ??= import(
			/* @vite-ignore */ new URL(AQUEDUCT_SLANG_BROWSER_HOST_PATH, location.origin).href
		);
		state.compiledWasmModulePromise ??= state.slangHostModulePromise.then((slangHost) =>
			slangHost.compileAqueductWebAssembly(
				new URL(AQUEDUCT_PROJECT_WASM_PATH, location.origin).href
			)
		);
		let module: WebAssembly.Module | undefined;
		try {
			module = await state.compiledWasmModulePromise;
		} catch (e) {
			console.error('Aqueduct: main-thread wasm compile failed; worker will compile:', e);
		}
		try {
			this.post({
				op: 'boot',
				consoleContext: pageConsoleContext(version),
				origin: location.origin,
				module,
				dev: import.meta.env.DEV,
				debugGpuBootFailures: debugGpuBootFailures()
			});
		} catch (e) {
			// A browser that cannot structured-clone a Module falls back to the
			// worker compiling for itself (same contract as the conformance
			// harness).
			if ((e as Error)?.name !== 'DataCloneError') throw e;
			this.post({
				op: 'boot',
				consoleContext: pageConsoleContext(version),
				origin: location.origin,
				dev: import.meta.env.DEV,
				debugGpuBootFailures: debugGpuBootFailures()
			});
		}
		this.checkRoleDrift();
	}

	private onMessage(msg: WorkerToMain) {
		if (this.terminalError) return;
		switch (msg.op) {
			case 'healthProbeResult':
				this.health.observer.probeAnswered(msg.id);
				for (const snapshot of msg.snapshots)
					this.health.observer.snapshot(snapshot.projectId, snapshot.generation, snapshot.state);
				for (const signal of msg.signals) this.health.receive(signal);
				return;
			case 'healthFailure': {
				if (this.health.generation(msg.projectId) !== msg.generation) return;
				const error: Error = new Error(msg.message);
				if (msg.stack) error.stack = msg.stack;
				this.health.observer.failure(msg.projectId, msg.category, msg.stage, error);
				return;
			}
			case 'booted':
				// Anchor worker-clock stage ends to navigation time for the
				// landing boot timeline (clocks share the epoch via timeOrigin).
				if (msg.workerTimeOrigin !== undefined) {
					for (const stage of msg.stages) {
						if (stage.at === undefined) continue;
						bootTimelineLog(`engine worker: ${stage.stage}`, {
							navMs: +(msg.workerTimeOrigin + stage.at - performance.timeOrigin).toFixed(1),
							ms: stage.ms,
							preboot: msg.preboot || undefined
						});
					}
				}
				if (msg.error) this.failWorker(new Error(msg.error));
				else this.bootedResolve(msg.stages);
				break;
			case 'queryResult': {
				const entry = this.pending.get(msg.id);
				if (!entry) return;
				this.pending.delete(msg.id);
				const error: Error | undefined = msg.ok ? undefined : new Error(msg.error);
				if (error && msg.stack) error.stack = msg.stack;
				if (error && msg.errorName) error.name = msg.errorName;
				this.health.queryFinished(entry.message, error, msg.expected);
				if (msg.ok) entry.resolve(msg.result);
				else entry.reject(new Error(msg.error));
				break;
			}
			case 'frame': {
				const handle = this.handles.get(msg.projectId);
				if (handle) {
					try {
						handle.receiveFrame(msg.packet as FramePacket);
						this.health.frame(msg.projectId, msg.packet);
					} catch (error: unknown) {
						this.health.observer.failure(msg.projectId, 'editor', 'presentation_pending', error);
					}
				} else {
					msg.packet.editorFrame?.foreground.close();
				}
				break;
			}
			case 'projectClosed':
				this.handles.delete(msg.projectId);
				break;
		}
	}

	post(message: MainToWorker, transfer: Transferable[] = []) {
		const msg: MainToWorker = this.health.prepare(message);
		if (this.terminalError) {
			if (msg.health) {
				this.health.observer.failure(
					msg.health.projectId,
					msg.health.category,
					'worker_unresponsive',
					this.terminalError,
					'command_rejected'
				);
				this.health.observer.complete(msg.health, true);
			}
			return;
		}
		try {
			this.health.dispatched(msg);
			this.worker.postMessage(msg, transfer);
		} catch (error: unknown) {
			if (msg.health) {
				this.health.failure(msg.health, error);
				this.health.observer.complete(msg.health, true);
			}
			throw error;
		}
	}

	query<T = unknown>(
		build: (id: number) => MainToWorker,
		transfer: Transferable[] = []
	): Promise<T> {
		const id = this.nextQueryId++;
		const msg: MainToWorker = this.health.prepare(build(id));
		this.health.queryStarted(msg);
		if (this.terminalError) {
			this.health.queryFinished(msg, this.terminalError);
			return Promise.reject(this.terminalError);
		}
		return new Promise<T>((resolve, reject) => {
			this.pending.set(id, {
				resolve: resolve as (v: unknown) => void,
				reject,
				label: messageLabel(msg),
				startedAt: performance.now(),
				message: msg
			});
			try {
				this.health.dispatched(msg);
				this.worker.postMessage(msg, transfer);
			} catch (e) {
				this.pending.delete(id);
				this.health.queryFinished(msg, e as Error);
				reject(e as Error);
			}
		});
	}

	/** A free function on the wasm module scope (listRegistry, normalizeAsset,
	 *  okhsv conversions, the node-definition compiler handles, ...). */
	moduleQuery<T = unknown>(
		method: string,
		args: unknown[] = [],
		transfer: Transferable[] = [],
		projectId: number = 0
	): Promise<T> {
		return this.query<T>(
			(id) => this.health.prepare({ op: 'moduleQuery', id, method, args }, projectId),
			transfer
		);
	}

	/** Push the rotating Firebase token; the worker never snapshots one. */
	setToken(token: string | null) {
		this.post({ op: 'setToken', token });
	}

	ensureSlangCompiler() {
		this.post({ op: 'ensureSlang' });
	}

	/** Seed the worker asset cache (drag-drop import already has the bytes). */
	seedAsset(projectId: number, url: string, bytes: Uint8Array, pendingUpload = false) {
		const copy = bytes.slice();
		this.post({ op: 'seedAsset', projectId, url, bytes: copy.buffer, pendingUpload }, [
			copy.buffer
		]);
	}

	confirmAssetUploaded(projectId: number, url: string): void {
		this.post({ op: 'assetUploaded', projectId, url });
	}

	/** A ready-to-use project instance (pooled or fresh, engine initialized in
	 *  the worker). Synchronous: work buffers behind the acquire message. */
	acquireProject(phoneResolutionPrompt = false, reportLoadingProgress = false): ProjectHandle {
		const projectId = this.nextProjectId++;
		const handle = new ProjectHandle(this, projectId);
		this.handles.set(projectId, handle);
		if (this.terminalError) handle.receiveTerminalError(this.terminalError);
		else
			this.post({ op: 'acquireProject', projectId, phoneResolutionPrompt, reportLoadingProgress });
		return handle;
	}

	openProject(options: {
		apiUrl: string;
		docId: string;
		clientId: string;
		phoneResolutionPrompt?: boolean;
		reportLoadingProgress?: boolean;
	}): ProjectHandle {
		const handle = this.acquireProject(
			options.phoneResolutionPrompt ?? false,
			options.reportLoadingProgress ?? false
		);
		handle.telemetry.documentId = options.docId;
		handle.telemetry.log('info', 'Project opened');
		this.post({ op: 'openProject', projectId: handle.projectId, ...options });
		return handle;
	}

	closeProject(handle: ProjectHandle | null | undefined) {
		if (!handle || handle.closed) return;
		handle.markClosed();
		this.post({ op: 'closeProject', projectId: handle.projectId });
	}
}

/** The page's engine broker (spawns the worker on first use). Browser only.
 *
 *  The singleton lives on `window`, not in module scope: module scope is
 *  per-instance, and dev-server HMR can serve this module under multiple
 *  timestamped URLs in one page, giving each instance its own scope. A
 *  module-scoped singleton then constructs a second broker, a second worker,
 *  and a second GPU device (only the first can adopt the pre-boot shell).
 *  The page-scoped slot guarantees one broker per page no matter how many
 *  instances of this module exist. */
export function getEngineBroker(): EngineBroker {
	if (!browser) throw new Error('EngineBroker is browser-only');
	const slot = window as unknown as { __aqueductEngineBroker?: EngineBroker };
	return (slot.__aqueductEngineBroker ??= new EngineBroker());
}
